import {
    Grid2
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import { ValidatorForm } from 'react-material-ui-form-validator';
import { ActualAuctionStatusEnum } from '../../../_common/enum/actualAuctionStatus';
import { CatalogueItemUserBidStatusEnum } from '../../../_common/enum/catalogueItemUserBidStatusEnum';
import { AuctionPostbackStatusEnum } from '../../../_common/enum/auctionPostbackStatus.enum';
import { MessageNotification } from '../../../_components/box/message-notification';
import { StandardButton } from '../../../_components/button/primary.button';
import { StandardPanel } from '../../../_components/panel/standard.panel';
import { StandardTextValidator } from '../../../_components/textValidator/standardTextValidator';
import { toastr } from '../../../_components/toastr/toastr';
import { handleErrorResponse } from '../../../_helpers/handleResponse.helper';
import { catalogueItemUserBidService } from '../../../_services/catalogueItemBidService.service';
import { CatalogueItemBidHistoryBox } from './catalogue-item-bid-history-box';

export function AuctionCatalogueItemUserBid(params: {
    catalogueItem?: any,
    actualAuctionStatusId: ActualAuctionStatusEnum,
    catalogueItemLoading: boolean
}) {

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [bid, setBid] = useState<string>("0");
    const [submittedBid, setSubmittedBid] = useState<string>("0");
    const [placedBids, setPlacedBids] = useState<string[]>([]);
    const isSubmittingRef = useRef(false);
    const lastItemIdRef = useRef<number | null>(null);

    useEffect(() => {
        if (!!params.catalogueItem) {
            setLoading(false);

            // Only reset the input when we move to a NEW catalogue item. Status
            // transitions (e.g. "Closing shortly") re-send the catalogue item
            // without the user's bid, and we must not wipe what they typed.
            const itemId = params.catalogueItem?.id ?? null;
            if (itemId !== lastItemIdRef.current) {
                lastItemIdRef.current = itemId;
                setBid(params.catalogueItem?.catalogueItemBid?.bid ?? "0");
                setSubmittedBid(clampBid(params.catalogueItem?.catalogueItemBid?.bid ?? "0"));
                setPlacedBids([]);
            }
        }
    }, [params.catalogueItem]);

    function handleChange(event: any) {
        const nextBid = event.target?.value ?? "0";
        setBid(nextBid);

        if (!params.catalogueItem?.id || isSubmittingRef.current) {
            return;
        }

        // Record every keystroke (best-effort) and swallow errors: this must never crash the page.
        catalogueItemUserBidService.typeBid(params.catalogueItem.id, nextBid).catch(() => { });
    }

    const BID_MIN = 0;
    const BID_MAX = 999;

    function clampBid(value: string): string {
        const n = parseFloat(value || "0");
        if (Number.isNaN(n)) return "0";
        const clamped = Math.min(BID_MAX, Math.max(BID_MIN, n));
        return clamped.toFixed(2);
    }

    function updateDecimals() {
        setBid(clampBid(bid));
    }

    function submit() {
        isSubmittingRef.current = true;
        setSaving(true);
        const clampedBid = clampBid(bid);
        setBid(clampedBid);
        return catalogueItemUserBidService.post(params.catalogueItem.id, clampedBid)
            .then(
                data => {
                    setSubmittedBid(clampedBid);
                    setPlacedBids((prev) => [clampedBid, ...prev]);
                    toastr.success("Bid placed")
                }, handleErrorResponse)
            .finally(() => {
                isSubmittingRef.current = false;
                setSaving(false);
            });
    }

    return (
        <Grid2 size={{ xs: 12 }} sx={{ '& .MuiCard-root': { margin: 0 } }}>
            <StandardPanel title={!!params.catalogueItemLoading ? "Loading.." : !!params.catalogueItem ? `Enter Bid` : undefined} loading={params.catalogueItemLoading}>

                {(!params.catalogueItemLoading && !params.catalogueItem) &&
                    <MessageNotification message='No active catalogue item selected' />
                }

                {(!params.catalogueItemLoading && !!params.catalogueItem) &&
                    <ValidatorForm
                        onSubmit={submit}
                        noValidate>

                        <Grid2 container spacing={{ xs: 0.5, sm: 1 }} sx={{ padding: { xs: '4px', sm: '8px' } }}>
                            <Grid2 size={{ xs: 12 }}>
                                <StandardTextValidator
                                    name="bid"
                                    label="Your Bid"
                                    fullWidth
                                    shrink
                                    step="0.01"
                                    type="number"
                                    value={bid}
                                    inputProps={{ min: BID_MIN, max: BID_MAX }}
                                    disabled={
                                        !(
                                            (params.actualAuctionStatusId === ActualAuctionStatusEnum.Active || params.actualAuctionStatusId === ActualAuctionStatusEnum.ClosingShortly) ||
                                            (params.actualAuctionStatusId === ActualAuctionStatusEnum.ReOffered && (params.catalogueItem?.catalogueItemBid?.catalogueItemUserBidStatusId === CatalogueItemUserBidStatusEnum.ReOffered || params.catalogueItem?.auctionPostbackStatusId === AuctionPostbackStatusEnum.ReOffered))
                                        )
                                    }
                                    reuired
                                    validators={['required', 'minNumber:0', 'maxNumber:999']}
                                    errorMessages={['required field', 'Minimum is 0', 'Maximum is 999']}
                                    onFocus={(e: any) => {
                                        const v = parseFloat(e.target?.value ?? "0");
                                        if (!v) setBid("");
                                    }}
                                    onBlur={updateDecimals}
                                    onChange={handleChange}
                                />
                            </Grid2>

                            <Grid2 size={{ xs: 12 }}>
                                <Grid2 container spacing={{ xs: 0.5, sm: 1 }}>
                                    <Grid2 size={{ xs: 12 }}>
                                        <StandardButton
                                            saving={saving}
                                            loading={loading}
                                            disabled={
                                                !(
                                                    (params.actualAuctionStatusId === ActualAuctionStatusEnum.Active || params.actualAuctionStatusId === ActualAuctionStatusEnum.ClosingShortly) ||
                                                    (params.actualAuctionStatusId === ActualAuctionStatusEnum.ReOffered && (params.catalogueItem?.catalogueItemBid?.catalogueItemUserBidStatusId === CatalogueItemUserBidStatusEnum.ReOffered || params.catalogueItem?.auctionPostbackStatusId === AuctionPostbackStatusEnum.ReOffered))
                                                )
                                            }
                                            icon="check"
                                            variant='success'
                                            type="submit">
                                            Submit your bid
                                        </StandardButton>
                                    </Grid2>
                                </Grid2>
                            </Grid2>


                        </Grid2>

                    </ValidatorForm>
                }

            </StandardPanel>
            <CatalogueItemBidHistoryBox submittedBid={submittedBid} placedBids={placedBids} />
        </Grid2>
    )
}
